<script setup lang="ts">
import { Link, useForm } from '@inertiajs/vue3';
import { ArrowLeft, ArrowRight, Building2, CheckCircle, Users } from 'lucide-vue-next';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import InputError from '@/components/InputError.vue';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import type { TenantType } from '@/config/constants/enums';
import { AUTH_ROUTES, MARKETING_ROUTES } from '@/config/constants/routes';
import { primeCsrfCookie } from '@/utils/csrf';

const { t } = useI18n();

defineProps<{
    audience: TenantType;
}>();

type View = 'cards' | 'form' | 'success';
const view = ref<View>('cards');

const form = useForm({
    name: '',
    email: '',
    company: '',
    message: '',
});

const showForm = () => {
    form.clearErrors();
    view.value = 'form';
};

const showCards = () => {
    view.value = 'cards';
};

const handleSubmit = async () => {
    // The homepage may arrive from the CDN edge with no session cookie; the
    // post needs one for its CSRF token (see utils/csrf.ts).
    await primeCsrfCookie();

    form.post(route(MARKETING_ROUTES.contactSales), {
        preserveScroll: true,
        onSuccess: () => {
            form.reset();
            view.value = 'success';
        },
    });
};

const sendAnother = () => {
    form.reset();
    form.clearErrors();
    view.value = 'form';
};
</script>

<template>
    <section class="dark:bg-background relative overflow-hidden bg-white py-20 sm:py-28 lg:py-40">
        <!-- Background Element -->
        <div class="pointer-events-none absolute inset-0 overflow-hidden">
            <svg
                class="absolute top-0 -left-1/4 h-full w-1/2 text-teal-300/20 dark:text-gray-800/20"
                viewBox="0 0 400 800"
                fill="none"
                preserveAspectRatio="xMidYMid slice"
            >
                <ellipse cx="200" cy="400" rx="300" ry="500" fill="currentColor" />
            </svg>
        </div>

        <div class="relative container mx-auto px-6 lg:px-8">
            <Transition name="slide-right-fade" mode="out-in">
                <div v-if="view === 'cards'" :key="'cards'" class="grid items-center gap-12 sm:gap-16 lg:grid-cols-2 lg:gap-24">
                    <!-- Content -->
                    <div>
                        <p
                            class="mb-4 font-medium tracking-widest text-gray-400 sm:mb-6 dark:text-gray-500"
                            style="font-size: 0.7rem; letter-spacing: 0.2em"
                        >
                            {{ t('home.cta.eyebrow') }}
                        </p>

                        <h2
                            class="font-display text-3xl leading-tight font-normal tracking-tight text-gray-900 sm:text-4xl md:text-5xl lg:text-6xl dark:text-white"
                        >
                            {{ t('home.cta.heading_before') }}
                            <span class="pr-[0.1em] text-orange-500 italic">{{ t('home.cta.heading_accent') }}</span>
                            {{ t('home.cta.heading_after') }}
                        </h2>

                        <Transition
                            mode="out-in"
                            enter-active-class="transition-all duration-300 ease-out"
                            enter-from-class="opacity-0 translate-y-1"
                            leave-active-class="transition-all duration-200 ease-in"
                            leave-from-class="opacity-100"
                            leave-to-class="opacity-0 -translate-y-1"
                        >
                            <p
                                :key="`cta-sub-${audience}`"
                                class="mt-6 max-w-lg text-base leading-relaxed text-gray-500 sm:mt-8 sm:text-lg dark:text-gray-400"
                            >
                                {{ t(`home.cta.${audience}.subheadline`) }}
                            </p>
                        </Transition>

                        <!-- Dual CTAs -->
                        <div class="mt-8 flex flex-col gap-4 sm:mt-12 sm:flex-row sm:items-center">
                            <Button
                                as-child
                                size="lg"
                                class="group h-12 rounded-none border-2 border-orange-500 bg-orange-500 px-8 text-sm font-medium tracking-wide text-white transition-all hover:bg-transparent hover:text-orange-600 sm:h-14 sm:px-10 sm:text-base dark:border-orange-400 dark:bg-orange-400 dark:text-gray-900 dark:hover:bg-transparent dark:hover:text-orange-400"
                            >
                                <Link :href="route(AUTH_ROUTES.register)">
                                    {{ t('home.cta.get_started') }}
                                    <ArrowRight class="ml-3 h-4 w-4 transition-transform group-hover:translate-x-1" />
                                </Link>
                            </Button>

                            <button
                                type="button"
                                class="px-6 py-3 text-left text-sm font-medium tracking-wide text-gray-500 underline-offset-4 transition-colors hover:text-gray-900 hover:underline dark:text-gray-400 dark:hover:text-white"
                                @click="showForm"
                            >
                                {{ t('home.cta.request_demo') }}
                            </button>
                        </div>
                    </div>

                    <!-- Visual -->
                    <div class="relative">
                        <div class="relative">
                            <!-- Flight path connecting supplier ↔ agent (desktop) -->
                            <div class="absolute top-0 -left-10 hidden h-full w-20 lg:block">
                                <!-- Teal dot aligned left of supplier icon -->
                                <div class="absolute top-[14%] right-[-20px] h-3 w-3 -translate-y-1/2 rounded-full bg-[#0097B2]"></div>
                                <!-- Path only (stretched to fill height) -->
                                <svg viewBox="0 0 60 200" fill="none" class="h-full w-full" preserveAspectRatio="none" aria-hidden="true">
                                    <path
                                        d="M52 28 L20 28 Q8 28 8 40 L8 122 Q8 134 20 134 L52 134"
                                        stroke="#0097B2"
                                        stroke-width="1.5"
                                        stroke-dasharray="4 5"
                                        stroke-linecap="round"
                                        fill="none"
                                    />
                                </svg>
                                <!-- Teal dot aligned left of agent icon -->
                                <div class="absolute top-[67%] right-[-20px] h-3 w-3 -translate-y-1/2 rounded-full bg-[#0097B2]"></div>
                            </div>

                            <div class="flex flex-col gap-4 sm:gap-6">
                                <!-- Supplier Card -->
                                <div
                                    class="group relative overflow-hidden rounded-sm border border-l-4 border-teal-200 border-l-teal-400 bg-teal-50/50 p-5 shadow-xs transition-all duration-300 hover:shadow-md hover:shadow-teal-900/5 sm:p-8 dark:border-gray-700 dark:border-l-gray-600 dark:bg-gray-900/80"
                                >
                                    <div class="flex items-start gap-4 sm:gap-6">
                                        <div
                                            class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-teal-100 text-teal-600 sm:h-14 sm:w-14 dark:bg-gray-800 dark:text-gray-300"
                                        >
                                            <Building2 class="h-5 w-5 sm:h-6 sm:w-6" />
                                        </div>
                                        <div class="min-w-0">
                                            <h3 class="font-display mb-1 text-lg font-normal text-gray-900 sm:mb-2 sm:text-xl dark:text-white">
                                                {{ t('home.cta.supplier.card_title') }}
                                            </h3>
                                            <p class="text-sm text-gray-500 sm:text-base dark:text-gray-400">
                                                {{ t('home.cta.supplier.card_description') }}
                                            </p>
                                            <div class="mt-3 flex flex-wrap gap-1.5 sm:mt-4 sm:gap-2">
                                                <span
                                                    class="rounded-full border border-teal-200 px-2.5 py-0.5 text-xs text-teal-600 sm:px-3 sm:py-1 dark:border-gray-600 dark:text-gray-300"
                                                >
                                                    {{ t('home.cta.supplier.tags.lodges') }}
                                                </span>
                                                <span
                                                    class="rounded-full border border-teal-200 px-2.5 py-0.5 text-xs text-teal-600 sm:px-3 sm:py-1 dark:border-gray-600 dark:text-gray-300"
                                                >
                                                    {{ t('home.cta.supplier.tags.hotels') }}
                                                </span>
                                                <span
                                                    class="rounded-full border border-teal-200 px-2.5 py-0.5 text-xs text-teal-600 sm:px-3 sm:py-1 dark:border-gray-600 dark:text-gray-300"
                                                >
                                                    {{ t('home.cta.supplier.tags.transfers') }}
                                                </span>
                                                <span
                                                    class="rounded-full border border-teal-200 px-2.5 py-0.5 text-xs text-teal-600 sm:px-3 sm:py-1 dark:border-gray-600 dark:text-gray-300"
                                                >
                                                    {{ t('home.cta.supplier.tags.activities') }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Mobile flight path between cards -->
                                <div class="flex justify-center lg:hidden">
                                    <svg viewBox="0 0 80 36" fill="none" class="h-9 w-20" aria-hidden="true">
                                        <circle cx="20" cy="5" r="4" fill="#0097B2" />
                                        <!-- down, bend left, horizontal left, bend down, down, bend right, horizontal right, bend up -->
                                        <path
                                            d="M20 9 L20 14 Q20 20 14 20 L10 20 Q4 20 4 26 L4 26 Q4 32 10 32 L70 32 Q76 32 76 26 L76 26 Q76 20 70 20 L64 20 Q60 20 60 14 L60 9"
                                            stroke="#0097B2"
                                            stroke-width="1.5"
                                            stroke-dasharray="3 4"
                                            stroke-linecap="round"
                                            fill="none"
                                        />
                                        <circle cx="60" cy="5" r="4" fill="#0097B2" />
                                    </svg>
                                </div>

                                <!-- Agent Card -->
                                <div
                                    class="group relative overflow-hidden rounded-sm border border-l-4 border-orange-200 border-l-orange-400 bg-orange-50/50 p-5 shadow-xs transition-all duration-300 hover:shadow-md hover:shadow-orange-900/5 sm:p-8 dark:border-gray-700 dark:border-l-gray-600 dark:bg-gray-900/80"
                                >
                                    <div class="flex items-start gap-4 sm:gap-6">
                                        <div
                                            class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-orange-100 text-orange-600 sm:h-14 sm:w-14 dark:bg-gray-800 dark:text-gray-300"
                                        >
                                            <Users class="h-5 w-5 sm:h-6 sm:w-6" />
                                        </div>
                                        <div class="min-w-0">
                                            <h3 class="font-display mb-1 text-lg font-normal text-gray-900 sm:mb-2 sm:text-xl dark:text-white">
                                                {{ t('home.cta.agent.card_title') }}
                                            </h3>
                                            <p class="text-sm text-gray-500 sm:text-base dark:text-gray-400">
                                                {{ t('home.cta.agent.card_description') }}
                                            </p>
                                            <div class="mt-3 flex flex-wrap gap-1.5 sm:mt-4 sm:gap-2">
                                                <span
                                                    class="rounded-full border border-orange-200 px-2.5 py-0.5 text-xs text-orange-600 sm:px-3 sm:py-1 dark:border-gray-600 dark:text-gray-300"
                                                >
                                                    {{ t('home.cta.agent.tags.tour_operators') }}
                                                </span>
                                                <span
                                                    class="rounded-full border border-orange-200 px-2.5 py-0.5 text-xs text-orange-600 sm:px-3 sm:py-1 dark:border-gray-600 dark:text-gray-300"
                                                >
                                                    {{ t('home.cta.agent.tags.dmcs') }}
                                                </span>
                                                <span
                                                    class="rounded-full border border-orange-200 px-2.5 py-0.5 text-xs text-orange-600 sm:px-3 sm:py-1 dark:border-gray-600 dark:text-gray-300"
                                                >
                                                    {{ t('home.cta.agent.tags.travel_companies') }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Contact Form -->
                <div
                    v-else-if="view === 'form'"
                    :key="'form'"
                    class="mx-auto w-full max-w-5xl overflow-hidden rounded-sm border border-l-4 border-orange-200 border-l-orange-400 bg-white p-6 shadow-xs sm:p-8 dark:border-gray-700 dark:border-l-orange-400 dark:bg-gray-900/80"
                >
                    <div class="mb-6 flex items-start justify-between gap-4">
                        <div>
                            <h3 class="font-display text-2xl leading-tight font-normal tracking-tight text-gray-900 sm:text-3xl dark:text-white">
                                {{ t('home.cta.form.title') }}
                            </h3>
                            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                {{ t('home.cta.form.description') }}
                            </p>
                        </div>
                        <button
                            type="button"
                            class="inline-flex shrink-0 items-center gap-2 text-xs font-medium tracking-wide text-gray-500 transition-colors hover:text-orange-600 dark:text-gray-400 dark:hover:text-orange-400"
                            @click="showCards"
                        >
                            <ArrowLeft class="h-3.5 w-3.5" />
                            {{ t('home.cta.form.back') }}
                        </button>
                    </div>

                    <form class="space-y-4" @submit.prevent="handleSubmit">
                        <div class="grid gap-4 sm:grid-cols-3">
                            <div class="space-y-1.5">
                                <Label for="cta-name">{{ t('home.cta.form.name_label') }}</Label>
                                <Input id="cta-name" v-model="form.name" :placeholder="t('home.cta.form.name_placeholder')" autocomplete="name" />
                                <InputError :message="form.errors.name" />
                            </div>
                            <div class="space-y-1.5">
                                <Label for="cta-email">{{ t('home.cta.form.email_label') }}</Label>
                                <Input
                                    id="cta-email"
                                    v-model="form.email"
                                    type="email"
                                    :placeholder="t('home.cta.form.email_placeholder')"
                                    autocomplete="email"
                                />
                                <InputError :message="form.errors.email" />
                            </div>
                            <div class="space-y-1.5">
                                <Label for="cta-company">{{ t('home.cta.form.company_label') }}</Label>
                                <Input
                                    id="cta-company"
                                    v-model="form.company"
                                    :placeholder="t('home.cta.form.company_placeholder')"
                                    autocomplete="organization"
                                />
                                <InputError :message="form.errors.company" />
                            </div>
                        </div>

                        <div class="space-y-1.5">
                            <Label for="cta-message">{{ t('home.cta.form.message_label') }}</Label>
                            <Textarea id="cta-message" v-model="form.message" :placeholder="t('home.cta.form.message_placeholder')" :rows="3" />
                            <InputError :message="form.errors.message" />
                        </div>

                        <Button
                            type="submit"
                            size="lg"
                            :disabled="form.processing"
                            class="group h-12 w-full rounded-none border-2 border-orange-500 bg-orange-500 px-8 text-sm font-medium tracking-wide text-white transition-all hover:bg-transparent hover:text-orange-600 sm:text-base dark:border-orange-400 dark:bg-orange-400 dark:text-gray-900 dark:hover:bg-transparent dark:hover:text-orange-400"
                        >
                            {{ form.processing ? t('home.cta.form.submitting') : t('home.cta.form.submit') }}
                            <ArrowRight v-if="!form.processing" class="ml-3 h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </Button>
                    </form>
                </div>

                <!-- Success -->
                <div
                    v-else
                    :key="'success'"
                    class="mx-auto flex w-full max-w-5xl flex-col items-center justify-center overflow-hidden rounded-sm border border-l-4 border-teal-200 border-l-teal-400 bg-teal-50/50 p-8 text-center shadow-xs sm:p-10 dark:border-gray-700 dark:border-l-teal-400 dark:bg-gray-900/80"
                >
                    <div class="flex h-14 w-14 items-center justify-center rounded-full bg-teal-100 dark:bg-gray-800">
                        <CheckCircle class="h-7 w-7 text-teal-600 dark:text-teal-400" />
                    </div>
                    <h3 class="font-display mt-4 text-2xl leading-tight font-normal tracking-tight text-gray-900 sm:text-3xl dark:text-white">
                        {{ t('home.cta.form.success_title') }}
                    </h3>
                    <p class="mt-2 max-w-xl text-sm text-gray-500 sm:text-base dark:text-gray-400">
                        {{ t('home.cta.form.success_description') }}
                    </p>
                    <button
                        type="button"
                        class="mt-4 text-xs font-medium tracking-wide text-gray-500 underline-offset-4 transition-colors hover:text-orange-600 hover:underline dark:text-gray-400 dark:hover:text-orange-400"
                        @click="sendAnother"
                    >
                        {{ t('home.cta.form.success_back') }}
                    </button>
                </div>
            </Transition>
        </div>
    </section>
</template>
